import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Ticket, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { Btn } from "../components/ui-kit";

export default function CheckIn() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [working, setWorking] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/paypal/tickets/checkin/${code}`)
      .then((r) => { if (alive) setInfo(r.data); })
      .catch(() => { if (alive) setInfo({ found: false }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code]);

  const doCheckIn = async () => {
    setWorking(true); setAuthError(false);
    try {
      const { data } = await api.post(`/paypal/tickets/checkin/${code}`);
      setDone(data);
      setInfo((p) => ({ ...(p || {}), checked_in: true, checked_in_at: data.checked_in_at }));
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) setAuthError(true);
    } finally { setWorking(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center px-5 py-16" data-testid="checkin-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 justify-center mb-8 text-white/70">
          <Ticket size={18} className="text-[#E63946]" />
          <span className="text-xs uppercase tracking-[0.25em]">One Love Ministries · Door Check-In</span>
        </div>

        <div className="bg-white text-[#0A0A0A] rounded-2xl overflow-hidden">
          <div className="h-1.5 bg-[#E63946]" />
          <div className="p-8">
            {loading && (
              <div className="py-10 grid place-items-center" data-testid="checkin-loading">
                <Loader2 className="animate-spin text-[#E63946]" size={30} />
              </div>
            )}

            {!loading && info && !info.found && (
              <div className="text-center py-6" data-testid="checkin-notfound">
                <XCircle size={48} className="text-[#E63946] mx-auto mb-4" />
                <h1 className="font-display font-bold text-2xl mb-2">Ticket Not Found</h1>
                <p className="text-black/60 text-sm">Code <span className="font-mono font-bold">{code}</span> did not match any ticket.</p>
              </div>
            )}

            {!loading && info && info.found && !info.valid && (
              <div className="text-center py-6" data-testid="checkin-unpaid">
                <ShieldAlert size={48} className="text-[#E63946] mx-auto mb-4" />
                <h1 className="font-display font-bold text-2xl mb-2">Not a Valid Ticket</h1>
                <p className="text-black/60 text-sm">This order has not been paid for.</p>
              </div>
            )}

            {!loading && info && info.found && info.valid && (
              <div data-testid="checkin-valid">
                <div className="text-center mb-6">
                  {info.checked_in ? (
                    <CheckCircle2 size={52} className="text-emerald-500 mx-auto mb-3" />
                  ) : (
                    <CheckCircle2 size={52} className="text-[#E63946] mx-auto mb-3" />
                  )}
                  <h1 className="font-display font-bold text-2xl">{info.event_title}</h1>
                </div>

                <div className="text-sm space-y-2 border-t border-black/10 pt-5">
                  <div className="flex justify-between"><span className="text-black/50">Guest</span><span className="font-medium">{info.name || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-black/50">Tickets</span><span className="font-medium">{info.quantity}</span></div>
                  <div className="flex justify-between"><span className="text-black/50">Code</span><span className="font-mono font-bold tracking-wider">{code}</span></div>
                </div>

                {info.checked_in ? (
                  <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center" data-testid="checkin-already">
                    <p className="font-bold text-emerald-700">
                      {done?.already_checked_in ? "Already Checked In" : "Checked In"}
                    </p>
                    {info.checked_in_at && (
                      <p className="text-xs text-emerald-600 mt-1">{new Date(info.checked_in_at).toLocaleString()}</p>
                    )}
                  </div>
                ) : (
                  <div className="mt-6">
                    <Btn onClick={doCheckIn} disabled={working} className="w-full" testid="checkin-btn">
                      {working ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                      {working ? "Checking in…" : "Mark as Checked In"}
                    </Btn>
                    {authError && (
                      <div className="mt-4 text-center text-sm" data-testid="checkin-auth-error">
                        <p className="text-[#E63946] mb-2">Please sign in as a church admin to check guests in.</p>
                        <Link to="/admin/login" className="underline font-medium">Go to Admin Login</Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-white/40 text-xs mt-6">ONE CHURCH. ONE FAMILY. ONE LOVE.</p>
      </div>
    </div>
  );
}
